import { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { Loader2, LogOut, MousePointerClick, Users, Lock } from "lucide-react";
import { blogPosts } from "../data/blogPosts";
import { hawaiiRegion } from "../data/hawaiiCities";
import { nevadaRegion } from "../data/nevadaCities";
import { californiaRegion } from "../data/californiaCities";
import { usePageMeta } from "./usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PERIODS = [{ d: 7, l: "7 days" }, { d: 30, l: "30 days" }, { d: 90, l: "90 days" }, { d: 0, l: "All time" }];

const LABELS = (() => {
  const m = { "/": "Homepage", "/blog": "Blog list" };
  blogPosts.forEach((p) => { m[`/blog/${p.slug}`] = `Article: ${p.title}`; });
  [californiaRegion, hawaiiRegion, nevadaRegion].forEach((r) => {
    m[r.basePath] = `${r.stateName} hub page`;
    r.cities.forEach((c) => { m[`${r.basePath}/${c.slug}`] = `${c.name}, ${r.abbr} page`; });
  });
  return m;
})();
const label = (path) => LABELS[path] || path;

const login = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/admin/leads";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const Gate = ({ title, text, action, testid }) => (
  <div className="min-h-[70vh] flex items-center justify-center px-6 pt-24" data-testid={testid}>
    <div className="bg-white rounded-3xl p-10 max-w-md w-full text-center border border-[#143628]/10">
      <Lock className="w-10 h-10 text-[#C45A41] mx-auto mb-4" />
      <h1 className="font-outfit font-bold text-2xl text-[#143628] mb-3">{title}</h1>
      <p className="font-manrope text-[#4A6359] mb-6">{text}</p>
      {action}
    </div>
  </div>
);

const RankTable = ({ title, rows, unit, testid }) => {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="bg-white rounded-3xl p-6 border border-[#143628]/10 min-w-0" data-testid={testid}>
      <h2 className="font-outfit font-bold text-lg text-[#143628] mb-4">{title}</h2>
      {rows.length === 0 ? <p className="font-manrope text-sm text-[#4A6359]">No data yet for this period.</p> : (
        <div className="space-y-3">
          {rows.slice(0, 15).map((r, i) => (
            <div key={r.page} data-testid={`${testid}-row-${i}`}>
              <div className="flex justify-between gap-4 font-manrope text-sm">
                <span className="text-[#143628] truncate min-w-0 flex-1" title={r.page}>{label(r.page)}</span>
                <span className="font-semibold text-[#143628] whitespace-nowrap">{r.count} {unit}</span>
              </div>
              <div className="h-1.5 bg-[#EBEAE5] rounded-full mt-1.5">
                <div className="h-1.5 bg-[#C45A41] rounded-full transition-[width] duration-500" style={{ width: `${(r.count / max) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Stat = ({ icon: Icon, value, text, testid }) => (
  <div className="bg-[#143628] rounded-3xl p-6 text-white" data-testid={testid}>
    <Icon className="w-6 h-6 text-[#F0B8A8] mb-3" />
    <div className="font-outfit font-[850] text-4xl">{value}</div>
    <div className="font-manrope text-white/70 text-sm mt-1">{text}</div>
  </div>
);

const RecentLeads = ({ leads }) => (
  <div className="bg-white rounded-3xl p-6 border border-[#143628]/10 overflow-x-auto" data-testid="recent-leads-table">
    <h2 className="font-outfit font-bold text-lg text-[#143628] mb-4">Recent quote requests</h2>
    {leads.length === 0 ? <p className="font-manrope text-sm text-[#4A6359]">No quote requests yet for this period.</p> : (
      <table className="w-full font-manrope text-sm min-w-[720px]">
        <thead><tr className="text-left text-[#4A6359] border-b border-[#143628]/10">
          <th className="py-2 pr-4">Date</th><th className="pr-4">Name</th><th className="pr-4">Contact</th><th className="pr-4">Type</th><th className="pr-4">Landed on</th><th>Form on</th>
        </tr></thead>
        <tbody>
          {leads.map((l, i) => (
            <tr key={i} className="border-b border-[#143628]/5 text-[#143628]" data-testid={`recent-lead-${i}`}>
              <td className="py-2 pr-4 whitespace-nowrap">{new Date(l.created_at).toLocaleDateString()}</td>
              <td className="pr-4">{l.name}</td>
              <td className="pr-4">{l.email}<br /><span className="text-[#4A6359]">{l.phone}</span></td>
              <td className="pr-4">{l.insurance_type}</td>
              <td className="pr-4">{l.landing_page ? label(l.landing_page) : "(not tracked)"}</td>
              <td>{l.form_page ? label(l.form_page) : "(not tracked)"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);

const SendNow = () => {
  const [state, setState] = useState("idle");
  const send = () => {
    setState("sending");
    axios.post(`${API}/admin/send-weekly-email`, {}, { withCredentials: true })
      .then(() => setState("sent")).catch(() => setState("error"));
  };
  return (
    <div className="bg-white rounded-3xl p-6 border border-[#143628]/10 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="weekly-email-card">
      <div>
        <h2 className="font-outfit font-bold text-lg text-[#143628]">Weekly email</h2>
        <p className="font-manrope text-sm text-[#4A6359]">A summary of the last 7 days is emailed every Monday at 8:00 AM Pacific.</p>
      </div>
      <button onClick={send} disabled={state === "sending"} data-testid="send-weekly-email-btn"
        className="whitespace-nowrap bg-[#143628] text-white px-6 py-2.5 rounded-full font-manrope text-sm hover:bg-[#1f4a37] transition-colors disabled:opacity-60">
        {state === "sending" ? "Sending..." : state === "sent" ? "Sent! Check your inbox" : state === "error" ? "Failed, try again" : "Email me this week's summary now"}
      </button>
    </div>
  );
};

const Report = ({ user, onLogout }) => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    axios.get(`${API}/admin/report`, { params: { days }, withCredentials: true }).then((r) => setData(r.data));
  }, [days]);

  return (
    <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-32 pb-20" data-testid="lead-report-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
        <div>
          <span className="uppercase text-xs font-manrope font-semibold tracking-[0.2em] text-[#C45A41]">Private</span>
          <h1 className="font-outfit font-[850] text-4xl sm:text-5xl text-[#143628] tracking-tight mt-2">Lead Source Report</h1>
          <p className="font-manrope text-[#4A6359] mt-2">Signed in as {user.email}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {PERIODS.map((p) => (
            <button key={p.d} onClick={() => setDays(p.d)} data-testid={`period-${p.d}`}
              className={`px-4 py-2 rounded-full font-manrope text-sm transition-colors duration-200 ${days === p.d ? "bg-[#143628] text-white" : "bg-white text-[#143628] border border-[#143628]/10 hover:bg-[#EBEAE5]"}`}>
              {p.l}
            </button>
          ))}
          <button onClick={onLogout} className="ml-2 inline-flex items-center gap-1 px-4 py-2 rounded-full font-manrope text-sm text-[#C45A41] hover:bg-[#C45A41]/10 transition-colors" data-testid="logout-btn">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </div>
      {!data ? <Loader2 className="w-8 h-8 animate-spin text-[#C45A41]" data-testid="report-loading" /> : (
        <div className="space-y-6">
          <div className="grid sm:grid-cols-2 gap-6">
            <Stat icon={Users} value={data.total_leads} text="Quote requests (form submissions)" testid="stat-total-leads" />
            <Stat icon={MousePointerClick} value={data.total_comparion_clicks} text="Clicks to your Comparion profile" testid="stat-comparion-clicks" />
          </div>
          <div className="grid lg:grid-cols-2 gap-6">
            <RankTable title="Quote requests by first page visited" rows={data.by_landing_page} unit="leads" testid="table-by-landing" />
            <RankTable title="Quote requests by page with the form" rows={data.by_form_page} unit="leads" testid="table-by-form" />
            <RankTable title="Comparion clicks by page clicked on" rows={data.clicks_by_page} unit="clicks" testid="table-clicks-by-page" />
            <RankTable title="Comparion clicks by first page visited" rows={data.clicks_by_landing_page} unit="clicks" testid="table-clicks-by-landing" />
          </div>
          <RecentLeads leads={data.recent_leads} />
          <SendNow />
          <p className="font-manrope text-xs text-[#4A6359]">Quote requests sent before this report was added show as "(not tracked)". Preview and live site keep separate data.</p>
        </div>
      )}
    </div>
  );
};

export default function AdminLeads() {
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [status, setStatus] = useState(location.state?.user ? "ok" : "checking");
  usePageMeta("Lead Source Report | Kenneth Carson", "Private lead report");

  const check = useCallback(() => {
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => { setUser(r.data); setStatus("ok"); })
      .catch(() => setStatus("anon"));
  }, []);

  useEffect(() => { if (!location.state?.user) check(); }, [check, location.state]);

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setUser(null); setStatus("anon");
  };

  if (status === "checking") return <div className="min-h-[70vh] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#C45A41]" /></div>;
  if (status === "anon") return (
    <Gate testid="admin-login-gate" title="Private lead report" text="Sign in with your Google account to see which pages bring in quote requests."
      action={<button onClick={login} className="bg-[#C45A41] text-white px-8 py-3 rounded-full font-manrope font-semibold hover:bg-[#A84D37] transition-colors" data-testid="google-login-btn">Sign in with Google</button>} />
  );
  if (!user.is_admin) return (
    <Gate testid="admin-no-access" title="No access" text={`${user.email} isn't on the approved list for this report.`}
      action={<button onClick={logout} className="text-[#C45A41] font-manrope font-semibold" data-testid="no-access-logout-btn">Sign out and try another account</button>} />
  );
  return <Report user={user} onLogout={logout} />;
}
